"use client";

import { GameCard } from "@/components/feature/game-card";
import { PointerInteractionAutoProvider } from "@/components/feature/pointer-interaction-media";
import { PreviewIntentAutoProvider } from "@/components/feature/preview-intent";
import type { PageOriginContext } from "@/lib/page-origin-context";
import type { GameWithRelations } from "@/types/game";

/** Matches games hub content: `container max-w-[1024px]`; 3 cols on small screens, 4 / 5 from md / lg. */
const HEADER_SEARCH_DROPDOWN_GRID =
  "grid grid-cols-3 gap-2 md:grid-cols-4 md:gap-3 lg:grid-cols-5 lg:gap-4";

interface HeaderSearchDropdownBodyProps {
  catalog: GameWithRelations[] | null;
  catalogLoadFailed: boolean;
  rankedGames: GameWithRelations[];
  isSearching: boolean;
  /** Trimmed query shown in the results heading (`Results for "…"`). */
  searchQuery: string;
  pageOrigin: PageOriginContext | null;
  /** Fired when a match is selected. */
  onResultSelect?: (game: GameWithRelations, position: number) => void;
}

function ResultsForHeading({ query }: { query: string }) {
  return (
    <h2 className="text-sm font-semibold text-white mb-2 break-words [overflow-wrap:anywhere]">
      {`Results for "${query}"`}
    </h2>
  );
}

function SearchGameGrid({
  games,
  testId,
  pageOrigin,
  onResultSelect,
}: {
  games: GameWithRelations[];
  testId: string;
  pageOrigin: PageOriginContext | null;
  onResultSelect?: (game: GameWithRelations, position: number) => void;
}) {
  return (
    <div className={HEADER_SEARCH_DROPDOWN_GRID} data-testid={testId}>
      {games.map((game, index) => (
        <div key={game.id} className="min-w-0 w-full aspect-square">
          <GameCard
            game={game}
            size="small"
            pageOrigin={pageOrigin}
            linkPrefetch={false}
            onLinkClick={onResultSelect ? () => onResultSelect(game, index) : undefined}
          />
        </div>
      ))}
    </div>
  );
}

export function HeaderSearchDropdownBody({
  catalog,
  catalogLoadFailed,
  rankedGames,
  isSearching,
  searchQuery,
  pageOrigin,
  onResultSelect,
}: HeaderSearchDropdownBodyProps) {
  const catalogReady = catalog !== null;
  const showNoMatchState =
    catalogReady && !catalogLoadFailed && rankedGames.length === 0 && isSearching;

  return (
    <div className="px-4 py-3 sm:px-5 sm:py-4">
      {!catalogReady && !catalogLoadFailed && (
        <p className="text-white/70 text-sm py-3 text-center">Loading games…</p>
      )}
      {catalogLoadFailed && (
        <p className="text-white/80 text-sm py-3 text-center">
          Couldn&apos;t load search. Please refresh and try again.
        </p>
      )}
      {catalogReady && rankedGames.length > 0 && (
        <PreviewIntentAutoProvider>
          <PointerInteractionAutoProvider>
            <ResultsForHeading query={searchQuery} />
            <SearchGameGrid
              games={rankedGames}
              testId="search-dropdown-matches"
              pageOrigin={pageOrigin}
              onResultSelect={onResultSelect}
            />
          </PointerInteractionAutoProvider>
        </PreviewIntentAutoProvider>
      )}
      {catalogReady && showNoMatchState && catalog.length === 0 && (
        <>
          <ResultsForHeading query={searchQuery} />
          <p
            className="text-white/80 text-sm py-3 text-center"
            data-testid="search-dropdown-library-empty"
          >
            No published games are available to search yet.
          </p>
        </>
      )}
      {catalogReady && showNoMatchState && catalog.length > 0 && (
        <div data-testid="search-dropdown-empty">
          <ResultsForHeading query={searchQuery} />
          <p
            className="text-white/80 text-sm py-3 text-center"
            data-testid="search-dropdown-no-matches-message"
            aria-live="polite"
          >
            No matches.
          </p>
        </div>
      )}
    </div>
  );
}
